import React, { useMemo, useState } from "react";
import { NAV } from "@/components/AppLayout";
import { ROLE_LABELS, landingPath } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionsContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ShieldCheck, Save, RotateCcw, Lock } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["admin", "branch_head", "cashier", "warehouse", "reseller"];

export default function MenuAccess() {
  const { disabled, savePerms } = usePermissions();
  const [local, setLocal] = useState(() => {
    const m = {};
    ROLES.forEach((r) => { m[r] = [...(disabled[r] || [])]; });
    return m;
  });
  const [saving, setSaving] = useState(false);

  // Menu yang bisa dikelola (punya setidaknya satu peran non-pemilik)
  const menus = useMemo(() => NAV.filter((n) => n.roles.some((r) => ROLES.includes(r))), []);

  const isEnabled = (role, path) => !(local[role] || []).includes(path);

  const toggle = (role, path) => {
    setLocal((prev) => {
      const list = new Set(prev[role] || []);
      if (list.has(path)) list.delete(path); else list.add(path);
      return { ...prev, [role]: [...list] };
    });
  };

  const reset = () => {
    const m = {};
    ROLES.forEach((r) => { m[r] = [...(disabled[r] || [])]; });
    setLocal(m);
    toast.message("Perubahan dibatalkan");
  };

  const save = async () => {
    setSaving(true);
    try {
      await savePerms(local);
      toast.success("Hak akses menu disimpan");
    } catch (e) {
      toast.error("Gagal menyimpan hak akses");
    } finally { setSaving(false); }
  };

  const dirty = ROLES.some((r) => {
    const a = new Set(local[r] || []); const b = new Set(disabled[r] || []);
    return a.size !== b.size || [...a].some((x) => !b.has(x));
  });

  return (
    <div className="p-6 space-y-6 animate-in-up" data-testid="menu-access-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-primary" /> Hak Akses Menu
          </h1>
          <p className="text-sm text-muted-foreground">Nonaktifkan menu tertentu untuk tiap peran. Pemilik selalu punya akses penuh.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} disabled={!dirty || saving} data-testid="reset-perms"><RotateCcw className="mr-2 h-4 w-4" /> Batalkan</Button>
          <Button onClick={save} disabled={!dirty || saving} data-testid="save-perms"><Save className="mr-2 h-4 w-4" /> {saving ? "Menyimpan..." : "Simpan"}</Button>
        </div>
      </div>

      <Card className="overflow-x-auto p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[180px]">Menu</TableHead>
              {ROLES.map((r) => (
                <TableHead key={r} className="text-center">{ROLE_LABELS[r]}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {menus.map((m) => {
              const Icon = m.icon;
              return (
                <TableRow key={m.to} data-testid={`perm-row-${m.to.slice(1)}`}>
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-2"><Icon className="h-4 w-4 text-muted-foreground" /> {m.label}</span>
                  </TableCell>
                  {ROLES.map((r) => {
                    if (!m.roles.includes(r)) return <TableCell key={r} className="text-center text-muted-foreground">—</TableCell>;
                    const isLanding = landingPath(r) === m.to;
                    if (isLanding) {
                      return (
                        <TableCell key={r} className="text-center">
                          <Badge variant="outline" className="gap-1 text-[10px]"><Lock className="h-3 w-3" /> Utama</Badge>
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell key={r} className="text-center">
                        <Switch
                          checked={isEnabled(r, m.to)}
                          onCheckedChange={() => toggle(r, m.to)}
                          data-testid={`perm-${r}-${m.to.slice(1)}`}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
      <p className="text-xs text-muted-foreground">Keterangan: aktif = menu tampil & dapat diakses · <Badge variant="outline" className="gap-1 text-[10px]"><Lock className="h-3 w-3" /> Utama</Badge> = halaman awal peran (tidak dapat dinonaktifkan).</p>
    </div>
  );
}
